/**
 * A2A Service for VocabRight
 * 
 * Service wrapper for A2A vocabulary capabilities
 */

import vocabRightClient, { executeTask } from '../a2aClient';

class A2AVocabRightService {
  constructor() {
    this.client = vocabRightClient;
  }

  /**
   * Get vocabulary sets
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of vocabulary sets
   */
  async getVocabularySets(params = {}) {
    console.log('[A2A Service] getVocabularySets: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      agentId: this.client.agentId,
      params,
      hasClient: !!this.client,
      capabilitiesLoaded: this.client.getCapabilities()?.length || 0
    });

    try {
      // Ensure capabilities are initialized
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        console.warn('[A2A Service] Capabilities not loaded yet, attempting to initialize...');
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_vocab_sets', params);
      
      console.log('[A2A Service] getVocabularySets: Success', {
        hasResult: !!result,
        hasSets: !!(result?.sets),
        setsCount: result?.sets?.length || 0,
        success: result?.success
      });
      
      // Handle different response formats
      if (result.success === false) {
        throw new Error(result.error || 'Failed to get vocabulary sets');
      }
      
      return result.sets || result.data || result;
    } catch (error) {
      console.error('[A2A Service] getVocabularySets: Error', {
        message: error.message,
        name: error.name,
        code: error.code,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Get user progress
   * Note: Backend doesn't have a dedicated get_vocab_progress capability
   * This extracts progress from calendar data or returns default structure
   * @param {Object} params - Query parameters (user_id, etc.)
   * @returns {Promise<Object>} User progress data
   */
  async getUserProgress(params = {}) {
    console.log('[A2A Service] getUserProgress: Using calendar data to extract progress', {
      serverUrl: this.client.remoteUrl,
      params
    });

    try {
      // Get calendar data which contains progress information
      const calendarData = await this.getCalendarData(params);
      
      // Extract progress from calendar data
      const completedSets = [];
      const calendar = calendarData.calendar || calendarData || {};
      
      // Build progress structure from calendar
      Object.keys(calendar).forEach(date => {
        const dayData = calendar[date];
        if (dayData && dayData.setId && !completedSets.includes(dayData.setId)) {
          completedSets.push(dayData.setId);
        }
      });
      
      const progress = {
        completedSets: completedSets,
        calendarData: calendar
      };
      
      console.log('[A2A Service] getUserProgress: Success', {
        completedSetsCount: completedSets.length
      });
      
      return progress;
    } catch (error) {
      console.error('[A2A Service] getUserProgress: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      // Return default progress structure on error
      return {
        completedSets: [],
        calendarData: {}
      };
    }
  }

  /**
   * Save user progress
   * @param {Object} progressData - Progress data to save
   * @returns {Promise<Object>} Saved progress data
   */
  async saveUserProgress(progressData) {
    console.log('[A2A Service] saveUserProgress: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      hasProgressData: !!progressData
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('update_vocab_progress', progressData);
      
      console.log('[A2A Service] saveUserProgress: Success', {
        hasResult: !!result,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to save user progress');
      }
      
      return result.progress || result.data || result;
    } catch (error) {
      console.error('[A2A Service] saveUserProgress: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Get calendar data
   * @param {Object} params - Query parameters (user_id, date_range, etc.)
   * @returns {Promise<Object>} Calendar data
   */
  async getCalendarData(params = {}) {
    console.log('[A2A Service] getCalendarData: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      params
    });

    try {
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_vocab_calendar', params);
      
      console.log('[A2A Service] getCalendarData: Success', {
        hasResult: !!result,
        success: result?.success
      });
      
      if (result.success === false) {
        throw new Error(result.error || 'Failed to get calendar data');
      }
      
      // Transform backend calendar format to VocabRight format
      // Backend returns: { calendar: { year, month, days: { "1": {...}, "2": {...} } } }
      // VocabRight expects: { "2025-01-15": { setId, status }, ... }
      const backendCalendar = result.calendar || {};
      const vocabRightCalendar = {};
      
      if (backendCalendar.days) {
        const year = backendCalendar.year || new Date().getFullYear();
        const month = backendCalendar.month || new Date().getMonth() + 1;
        
        Object.keys(backendCalendar.days).forEach(day => {
          const dayData = backendCalendar.days[day];
          const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          
          // Transform to VocabRight format
          // If setsCompleted > 0, mark as completed (DS = Done Successfully)
          if (dayData.setsCompleted > 0) {
            vocabRightCalendar[dateStr] = {
              setId: dayData.setId || null,
              status: 'DS', // Done Successfully
              wordsLearned: dayData.wordsLearned || 0
            };
          }
        });
      }
      
      return vocabRightCalendar;
    } catch (error) {
      console.error('[A2A Service] getCalendarData: Error', {
        message: error.message,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Detect user context
   * @returns {Object} User context
   */
  detectContext() {
    return this.client.detectContext();
  }

  /**
   * Negotiate UX format
   * @param {Object} context - User context
   * @returns {Promise<Object>} UX format recommendations
   */
  async negotiateUX(context = null) {
    const userContext = context || this.detectContext();
    return await this.client.negotiateUX(userContext);
  }
}

// Create singleton instance
const a2aVocabRightService = new A2AVocabRightService();

export default a2aVocabRightService;

